export const PYTHON_SCENARIOS_PROBLEMS = [
    {
      id: 1,
      title: "Parse CSV String with Quoted Delimiters",
      slug: "python-scenario-parse-csv-quoted-delimiters",
      seoTitle: "Python Parse Complex CSV String with Quotes | Repractiq",
      metaDescription: "Write a robust CSV string parser that correctly handles commas wrapped inside quoted values.",
      tags: ["scenarios", "string manipulation", "data parsing"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `parse_csv_line(csv_line)` that takes a single comma-separated line containing potential quoted elements (e.g., `'1,\"Smith, John\",45'`) and returns a clean list of individual fields without the surrounding double quotes.",
      explanation: "Standard splitting on commas fails when comma separators are contained within quoted text blocks. Implementing a state tracker or standard library solution handles nested quotes gracefully.",
      hint: "You can import and use the native `csv` module with `StringIO` to parse the string safely without writing a complex state machine manually.",
      starterCode: `def parse_csv_line(csv_line):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'1,\"Smith, John\",45'", call: "parse_csv_line('1,\"Smith, John\",45')", expected: "['1', 'Smith, John', '45']" },
        { input: "'a,b,\"c,d\"'", call: "parse_csv_line('a,b,\"c,d\"')", expected: "['a', 'b', 'c,d']" }
      ],
    },
    {
      id: 2,
      title: "Calculate Category Averages from Product Inventory",
      slug: "python-scenario-calculate-category-averages",
      seoTitle: "Python Dynamic Dictionary Grouping Average | Repractiq",
      metaDescription: "Group product inventories dynamically and calculate precise metric averages per category.",
      tags: ["scenarios", "dictionaries", "aggregation"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `category_averages(products)` that processes a list of dictionaries, where each item has 'name', 'category', and 'price'. Return a dictionary containing the average price for each category rounded to two decimal places.",
      explanation: "Grouping and dividing items dynamically handles multi-level inventory summaries regardless of incoming category keys.",
      hint: "Maintain a running total and count map for each unique category, then perform a final loop to compute averages.",
      starterCode: `def category_averages(products):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'category': 'tech', 'price': 100}, {'category': 'tech', 'price': 200}, {'category': 'home', 'price': 50}]", call: "dict(sorted(category_averages([{'category': 'tech', 'price': 100}, {'category': 'tech', 'price': 200}, {'category': 'home', 'price': 50}]).items()))", expected: "{'home': 50.0, 'tech': 150.0}" }
      ],
    },
    {
      id: 3,
      title: "Filter High-Value Orders with Complex Criteria",
      slug: "python-scenario-filter-orders-complex-criteria",
      seoTitle: "Python Multi Condition Record Filter | Repractiq",
      metaDescription: "Filter complex order documents using multiple logical conditions across nested status properties.",
      tags: ["scenarios", "filtering", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `filter_orders(orders, min_value, target_status)` that filters a list of order dicts. Keep only orders where the value of item quantities * individual prices exceeds `min_value`, and the status matches `target_status`.",
      explanation: "Filtering based on calculated inline expressions ensures high-value tracking stays updated across live configurations.",
      hint: "Calculate total value per order as: `quantity * price` before evaluating the comparison logic.",
      starterCode: `def filter_orders(orders, min_value, target_status):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'id': 1, 'qty': 5, 'price': 10, 'status': 'shipped'}, {'id': 2, 'qty': 2, 'price': 5, 'status': 'shipped'}], 30, 'shipped'", call: "filter_orders([{'id': 1, 'qty': 5, 'price': 10, 'status': 'shipped'}, {'id': 2, 'qty': 2, 'price': 5, 'status': 'shipped'}], 30, 'shipped')", expected: "[{'id': 1, 'qty': 5, 'price': 10, 'status': 'shipped'}]" }
      ],
    },
    {
      id: 4,
      title: "Group Transactions by Year and Month",
      slug: "python-scenario-group-transactions-by-date",
      seoTitle: "Python ISO Date Parsing and Grouping | Repractiq",
      metaDescription: "Parse ISO datestamps dynamically to group corporate transaction streams into temporal buckets.",
      tags: ["scenarios", "group by", "string manipulation"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `group_transactions(transactions)` that processes list items containing `{'date': 'YYYY-MM-DD', 'amount': float}` and aggregates the total amount grouped into a nested dictionary structure structured as `{ 'YYYY': { 'MM': total_amount } }`.",
      explanation: "Splitting date keys down to year and month segments allows for structured financial timeline breakdowns.",
      hint: "Use `.split('-')` on the date key to extract the year and month components.",
      starterCode: `def group_transactions(transactions):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'date': '2026-07-14', 'amount': 150}, {'date': '2026-07-20', 'amount': 50}]", call: "group_transactions([{'date': '2026-07-14', 'amount': 150}, {'date': '2026-07-20', 'amount': 50}])", expected: "{'2026': {'07': 200}}" }
      ],
    },
    {
      id: 5,
      title: "Find Top N Sales Agents with Value Ties",
      slug: "python-scenario-top-n-sales-agents",
      seoTitle: "Python Multi Tier Tie Breaker Ranking | Repractiq",
      metaDescription: "Rank sales performers dynamically using secondary alphabetical tie-breakers for equal numbers.",
      tags: ["scenarios", "sorting", "find top n"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `top_sales_agents(sales, n)` that identifies the top `n` agents based on sales volumes. If volumes are identical, resolve ties alphabetically by agent name.",
      explanation: "Implementing compound sorting keys ensures consistent ranking logic even when numerical metrics are identical.",
      hint: "Use a key function returning a tuple containing the negative sales amount followed by the string name: `(-x['sales'], x['name'])`.",
      starterCode: `def top_sales_agents(sales, n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'name': 'Bob', 'sales': 100}, {'name': 'Alice', 'sales': 100}, {'name': 'Charlie', 'sales': 200}], 2", call: "top_sales_agents([{'name': 'Bob', 'sales': 100}, {'name': 'Alice', 'sales': 100}, {'name': 'Charlie', 'sales': 200}], 2)", expected: "[{'name': 'Charlie', 'sales': 200}, {'name': 'Alice', 'sales': 100}]" }
      ],
    },
    {
      id: 6,
      title: "Calculate Running Account Balance with Draft Protection",
      slug: "python-scenario-running-balance-draft-protection",
      seoTitle: "Python Cumulative Balance State Machine | Repractiq",
      metaDescription: "Compute cumulative ledger running balances over transaction histories with safety thresholds.",
      tags: ["scenarios", "running total"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `running_balance(initial, ledger)` that takes an initial balance and list of transactions (positive or negative floats). Return a list representing the balance after each transaction, rejecting transactions that would cause the balance to fall below zero.",
      explanation: "Simulating structural transactional updates protects financial models from entering negative state margins.",
      hint: "Only modify the active balance and append to the output if the resulting balance is greater than or equal to 0.",
      starterCode: `def running_balance(initial, ledger):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "100, [50, -200, 20]", call: "running_balance(100, [50, -200, 20])", expected: "[150, 170]" }
      ],
    },
    {
      id: 7,
      title: "Merge Multi-Source Inventory Ledgers",
      slug: "python-scenario-merge-inventory-ledgers",
      seoTitle: "Python Merge Duplicate Key Inventories | Repractiq",
      metaDescription: "Consolidate duplicate database record entries by merging item quantities and selecting highest price thresholds.",
      tags: ["scenarios", "merge datasets", "dictionaries"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `merge_inventories(store_a, store_b)` that merges two inventory maps. For matching keys, aggregate total quantities and retain the higher price value.",
      explanation: "Consolidating distributed warehouses requires resolving key collisions securely while preserving price advantages.",
      hint: "Extract keys from both structures, update matching ones manually, and preserve non-colliding records.",
      starterCode: `def merge_inventories(store_a, store_b):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'A': {'qty': 5, 'price': 10}}, {'A': {'qty': 2, 'price': 12}}", call: "merge_inventories({'A': {'qty': 5, 'price': 10}}, {'A': {'qty': 2, 'price': 12}})", expected: "{'A': {'qty': 7, 'price': 12}}" }
      ],
    },
    {
      id: 8,
      title: "Clean Dirty Metadata Fields",
      slug: "python-scenario-clean-dirty-metadata",
      seoTitle: "Python Clean Dirty Data Strings | Repractiq",
      metaDescription: "Sanitize input payloads containing mixed capitalization, extra whitespace, and missing values.",
      tags: ["scenarios", "clean dirty data"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `clean_metadata(records)` that processes metadata items, formatting all name strings to title case, stripping trailing spaces, and substituting a fallback value of `'N/A'` for missing values or empty strings.",
      explanation: "Standardizing dynamic incoming payloads prepares unstructured datasets for clean database indexing.",
      hint: "Use `.strip().title()` to clean the strings and check if a field is empty or `None` to apply the default fallback.",
      starterCode: `def clean_metadata(records):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'name': '  john smith  '}, {'name': ''}]", call: "clean_metadata([{'name': '  john smith  '}, {'name': ''}])", expected: "[{'name': 'John Smith'}, {'name': 'N/A'}]" }
      ],
    },
    {
      id: 9,
      title: "Generate Monospace Tabular Text Reports",
      slug: "python-scenario-generate-text-reports",
      seoTitle: "Python Monospace Table Text Alignment | Repractiq",
      metaDescription: "Format records into perfectly aligned plain text monospace tables for terminal rendering.",
      tags: ["scenarios", "format reports"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `format_text_table(data)` that takes a list of lists representing rows (where the first row is the header) and outputs a monospace formatted string with fields padded dynamically to fit the maximum column width.",
      explanation: "Dynamic padding aligns columns perfectly, producing highly readable monospace text layouts for terminals and logs.",
      hint: "Determine the maximum character width for each column index, and pad strings using `.ljust(width)`.",
      starterCode: `def format_text_table(data):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[['ID', 'Name'], ['1', 'John'], ['100', 'Alice']]", call: "format_text_table([['ID', 'Name'], ['1', 'John'], ['100', 'Alice']])", expected: "'ID  | Name \\n1   | John \\n100 | Alice'" }
      ],
    },
    {
      id: 10,
      title: "Aggregate Multivariable Campaign Performance Metrics",
      slug: "python-scenario-aggregate-campaign-metrics",
      seoTitle: "Python Aggregate Ad Campaign Metrics | Repractiq",
      metaDescription: "Consolidate raw marketing performance records to calculate aggregate CTR and conversion rates.",
      tags: ["scenarios", "aggregate metrics"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `aggregate_metrics(campaign_logs)` that summarizes click records to compute aggregate performance metrics: total impressions, total clicks, and the overall click-through rate (CTR) as a percentage rounded to two decimal places.",
      explanation: "Aggregating individual event logs yields the clean, high-level metrics required for analytical dashboards.",
      hint: "Sum up clicks and impressions separately first, then calculate CTR as `(clicks / impressions) * 100`.",
      starterCode: `def aggregate_metrics(campaign_logs):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'impressions': 1000, 'clicks': 50}, {'impressions': 2000, 'clicks': 100}]", call: "aggregate_metrics([{'impressions': 1000, 'clicks': 50}, {'impressions': 2000, 'clicks': 100}])", expected: "{'impressions': 3000, 'clicks': 150, 'ctr': 5.0}" }
      ],
    },
    {
      id: 11,
      title: "Parse CSV Log Stream with Mismatched Headers",
      slug: "python-scenario-csv-mismatched-headers",
      seoTitle: "Python Safe Tabular CSV Row Mapping | Repractiq",
      metaDescription: "Safely parse mismatched CSV records by aligning values against a structured master schema header.",
      tags: ["scenarios", "data parsing"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `parse_irregular_csv(header, rows)` that maps irregular comma-delimited row inputs against a master header schema. Fill missing columns with `None` to keep row lengths consistent.",
      explanation: "Mapping CSV entries against structural master templates ensures data remains complete even when rows are missing values.",
      hint: "Split the header line first to determine the reference length, then align row inputs accordingly.",
      starterCode: `def parse_irregular_csv(header, rows):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'id,name,role', ['1,Bob', '2,Alice,Admin']", call: "parse_irregular_csv('id,name,role', ['1,Bob', '2,Alice,Admin'])", expected: "[{'id': '1', 'name': 'Bob', 'role': None}, {'id': '2', 'name': 'Alice', 'role': 'Admin'}]" }
      ],
    },
    {
      id: 12,
      title: "Compute Dynamic Rolling Averages for Stock Tickers",
      slug: "python-scenario-rolling-ticker-averages",
      seoTitle: "Python Rolling Window Average Logic | Repractiq",
      metaDescription: "Calculate rolling price averages over time-series data streams using a sliding window.",
      tags: ["scenarios", "aggregation"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `rolling_ticker_averages(prices, window_size)` that processes a chronological list of stock price floats, returning a list of rolling average values over the specified window size, rounded to two decimal places.",
      explanation: "Sliding window calculations smooth out noise in volatile data streams, making it easier to analyze overall trends.",
      hint: "Use slice ranges like `prices[i - window_size + 1 : i + 1]` to calculate each window's average once the minimum window length is met.",
      starterCode: `def rolling_ticker_averages(prices, window_size):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[10, 20, 30, 40], 2", call: "rolling_ticker_averages([10, 20, 30, 40], 2)", expected: "[15.0, 25.0, 35.0]" }
      ],
    },
    {
      id: 13,
      title: "Advanced Filter for Expired Subscriptions",
      slug: "python-scenario-filter-expired-subscriptions",
      seoTitle: "Python Multi Condition Expiry Filter | Repractiq",
      metaDescription: "Identify expired user records by verifying membership attributes against a baseline evaluation date.",
      tags: ["scenarios", "filtering"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `filter_expired_accounts(accounts, reference_date)` that filters user dictionaries. Keep only records where 'expiration_date' is strictly earlier than `reference_date` (both formatted as 'YYYY-MM-DD') and 'auto_renew' is False.",
      explanation: "Filtering based on date string comparisons is a reliable way to automate membership and subscription status checks.",
      hint: "Standard ISO-8601 date strings ('YYYY-MM-DD') can be compared directly using Python's relational operators.",
      starterCode: `def filter_expired_accounts(accounts, reference_date):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'user': 'A', 'expiration_date': '2026-06-01', 'auto_renew': False}, {'user': 'B', 'expiration_date': '2026-08-01', 'auto_renew': False}], '2026-07-14'", call: "filter_expired_accounts([{'user': 'A', 'expiration_date': '2026-06-01', 'auto_renew': False}, {'user': 'B', 'expiration_date': '2026-08-01', 'auto_renew': False}], '2026-07-14')", expected: "[{'user': 'A', 'expiration_date': '2026-06-01', 'auto_renew': False}]" }
      ],
    },
    {
      id: 14,
      title: "Group Nested Server Logs by Response Category",
      slug: "python-scenario-group-server-logs-by-status",
      seoTitle: "Python Nested Status Code Aggregator | Repractiq",
      metaDescription: "Analyze and group server response logs dynamically based on HTTP status code ranges.",
      tags: ["scenarios", "group by"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `group_by_status_class(logs)` that groups server log dicts based on their HTTP status code range (e.g., codes in the 200s group as '2xx', 400s as '4xx', etc.), returning a dictionary listing matching log payloads.",
      explanation: "Grouping errors and responses into broad status classes makes it much easier to monitor overall system health.",
      hint: "Determine the category by dividing the status integer by 100 and appending 'xx'.",
      starterCode: `def group_by_status_class(logs):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'url': '/home', 'status': 200}, {'url': '/login', 'status': 404}]", call: "dict(sorted(group_by_status_class([{'url': '/home', 'status': 200}, {'url': '/login', 'status': 404}]).items()))", expected: "{'2xx': [{'url': '/home', 'status': 200}], '4xx': [{'url': '/login', 'status': 404}]}" }
      ],
    },
    {
      id: 15,
      title: "Identify Top N Most Visited Pages with Ties",
      slug: "python-scenario-top-visited-pages-ties",
      seoTitle: "Python Key Count Sort Frequency | Repractiq",
      metaDescription: "Identify the top N visited pages from log entries using clean alphabetical sorting to resolve traffic volume ties.",
      tags: ["scenarios", "find top n", "sorting"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `top_visited_pages(logs, n)` that counts occurrences of nested page URL logs and returns the top `n` records, using alphabetical sorting as a tie-breaker.",
      explanation: "Custom frequency ranking lists must handle volume ties gracefully to ensure consistent, predictable dashboard reporting.",
      hint: "Count frequencies using a dictionary, then sort the items using the compound key: `(-count, page_name)`.",
      starterCode: `def top_visited_pages(logs, n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "['/home', '/about', '/home', '/contact'], 2", call: "top_visited_pages(['/home', '/about', '/home', '/contact'], 2)", expected: "[('/home', 2), ('/about', 1)]" }
      ],
    },
    {
      id: 16,
      title: "Calculate running storage changes with limits",
      slug: "python-scenario-running-storage-changes-limits",
      seoTitle: "Python Storage Allocation Cap Monitor | Repractiq",
      metaDescription: "Calculate dynamic storage allocation changes while enforcing strict maximum capacity constraints.",
      tags: ["scenarios", "running total"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `running_storage_allocation(capacity, modifications)` that models system storage updates over time, tracking allocation progress and capping modifications at the maximum capacity limit.",
      explanation: "Enforcing safety thresholds prevents storage configurations from overflowing during large bulk operations.",
      hint: "Accumulate changes in a loop, and limit the cumulative balance so it never exceeds the total capacity ceiling.",
      starterCode: `def running_storage_allocation(capacity, modifications):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "100, [40, 70, -20]", call: "running_storage_allocation(100, [40, 70, -20])", expected: "[40, 100, 80]" }
      ],
    },
    {
      id: 17,
      title: "Merge Nested Client Config Profiles",
      slug: "python-scenario-merge-client-profiles",
      seoTitle: "Python Merge Nested User Attribute Maps | Repractiq",
      metaDescription: "Merge multi-layered client profile objects together, overwriting properties dynamically with values from the source of truth.",
      tags: ["scenarios", "merge datasets", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `merge_profiles(primary, secondary)` that merges two dictionary profiles. The primary dictionary's values should override the secondary dictionary's values for any matching keys.",
      explanation: "Merging user settings across different sources requires a clear priority order to maintain accurate, up-to-date user preferences.",
      hint: "Create a copy of the secondary dictionary first, then use the `.update()` method with the primary dictionary to apply the overrides.",
      starterCode: `def merge_profiles(primary, secondary):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'id': 1, 'theme': 'dark'}, {'id': 1, 'theme': 'light', 'lang': 'en'}", call: "dict(sorted(merge_profiles({'id': 1, 'theme': 'dark'}, {'id': 1, 'theme': 'light', 'lang': 'en'}).items()))", expected: "{'id': 1, 'lang': 'en', 'theme': 'dark'}" }
      ],
    },
    {
      id: 18,
      title: "Sanitize Corrupted Numeric Data Rows",
      slug: "python-scenario-sanitize-corrupted-numeric-rows",
      seoTitle: "Python Validate Nested String Float Inputs | Repractiq",
      metaDescription: "Identify and filter out corrupted rows from text data streams by verifying clean numeric formatting.",
      tags: ["scenarios", "clean dirty data"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `clean_corrupted_rows(rows)` that filters out data rows containing non-numeric values where float measurements are expected, returning a list of cleaned, parsed records.",
      explanation: "Filtering out invalid numeric strings early prevents math operations from throwing exceptions during calculation steps.",
      hint: "Use a try-except block inside your loop to check if a value can be converted using `float(val)`.",
      starterCode: `def clean_corrupted_rows(rows):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[['1.5'], ['bad'], ['2.0']]", call: "clean_corrupted_rows([['1.5'], ['bad'], ['2.0']])", expected: "[[1.5], [2.0]]" }
      ],
    },
    {
      id: 19,
      title: "Format Fixed-Width Ledger System Prints",
      slug: "python-scenario-format-fixed-width-ledger",
      seoTitle: "Python Fixed Width Console Printing | Repractiq",
      metaDescription: "Format financial transactions into neat, fixed-width printed rows with clean alignment.",
      tags: ["scenarios", "format reports"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `format_ledger_row(id_str, amount, desc)` that formats transaction details into a fixed 30-character monospace string, reserving 5 spaces for the ID, 10 spaces for the amount, and aligning the remaining spaces for description details.",
      explanation: "Fixed-width layout functions align text fields precisely, which is critical for clear, professional terminal outputs and invoices.",
      hint: "Use string formatting options like `f'{id_str:<5}{amount:<10}{desc:<15}'` to format the columns.",
      starterCode: `def format_ledger_row(id_str, amount, desc):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'10', '150.00', 'Payment'", call: "format_ledger_row('10', '150.00', 'Payment')", expected: "'10   150.00    Payment        '" }
      ],
    },
    {
      id: 20,
      title: "Aggregate Multilevel Server Node Stats",
      slug: "python-scenario-aggregate-server-stats",
      seoTitle: "Python Nested Core Metric Math | Repractiq",
      metaDescription: "Compute aggregate usage statistics across multiple active cluster node arrays to calculate overall system loads.",
      tags: ["scenarios", "aggregate metrics"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `aggregate_server_metrics(nodes)` that processes cluster node statistics and returns a summary containing the overall average CPU and RAM usage percentage.",
      explanation: "Aggregating performance metrics from across multiple cluster nodes yields a clear picture of overall system performance.",
      hint: "Iterate through each node, sum up the metric parameters, and divide by the total number of items.",
      starterCode: `def aggregate_server_metrics(nodes):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'cpu': 40, 'ram': 60}, {'cpu': 80, 'ram': 80}]", call: "aggregate_server_metrics([{'cpu': 40, 'ram': 60}, {'cpu': 80, 'ram': 80}])", expected: "{'avg_cpu': 60.0, 'avg_ram': 70.0}" }
      ],
    },
    {
      id: 21,
      title: "Parse CSV Files with Varying Quotes and Spacing",
      slug: "python-scenario-parse-complex-spaces-csv",
      seoTitle: "Python Complex White Space CSV Parser | Repractiq",
      metaDescription: "Parse complex text files by trimming irregular spacing and stripping enclosing quote characters.",
      tags: ["scenarios", "data parsing", "string manipulation"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `parse_whitespace_csv(csv_text)` that parses comma-separated entries, strips away unexpected whitespace around columns, and removes any outer quote characters from strings.",
      explanation: "Trimming irregular spacing and stripping enclosing quotes ensures consistent database entries during bulk imports.",
      hint: "Iterate through the split fields, applying `.strip()` and removing matching start and end quotes.",
      starterCode: `def parse_whitespace_csv(csv_text):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'  \"user1\"  ,   \"admin\"  ,  \"active\" '", call: "parse_whitespace_csv('  \"user1\"  ,   \"admin\"  ,  \"active\" ')", expected: "['user1', 'admin', 'active']" }
      ],
    },
    {
      id: 22,
      title: "Calculate Average Session Durations",
      slug: "python-scenario-average-session-durations",
      seoTitle: "Python Dynamic Session Timeline Averages | Repractiq",
      metaDescription: "Calculate average page session durations by grouping and comparing timestamps across raw website logs.",
      tags: ["scenarios", "aggregation", "group by"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `average_durations(session_logs)` that groups session records by user ID and calculates the average active session duration (rounded to one decimal place) across those grouped records.",
      explanation: "Grouping timeline logs helps measure overall user retention and engagement patterns on your website.",
      hint: "Store lists of durations inside a temporary dictionary where the user ID serves as the grouping key.",
      starterCode: `def average_durations(session_logs):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'user_id': 1, 'duration': 120}, {'user_id': 1, 'duration': 180}, {'user_id': 2, 'duration': 60}]", call: "dict(sorted(average_durations([{'user_id': 1, 'duration': 120}, {'user_id': 1, 'duration': 180}, {'user_id': 2, 'duration': 60}]).items()))", expected: "{1: 150.0, 2: 60.0}" }
      ],
    },
    {
      id: 23,
      title: "Filter Accounts by Active Security Policy Criteria",
      slug: "python-scenario-filter-active-security-policies",
      seoTitle: "Python Complex Multi Layer Record Filter | Repractiq",
      metaDescription: "Identify and filter active security policy items that exceed specific storage or credential thresholds.",
      tags: ["scenarios", "filtering"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `filter_at_risk_users(users)` that extracts active user accounts that have multi-factor authentication (MFA) disabled and have gone more than 90 days since their last password change.",
      explanation: "Filtering user databases by security risk factors makes it easy to flag and remediate high-risk accounts.",
      hint: "Filter for records where `mfa_enabled == False` and `days_since_pw_change > 90` both evaluate to True.",
      starterCode: `def filter_at_risk_users(users):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'name': 'Bob', 'mfa_enabled': False, 'days_since_pw_change': 100}, {'name': 'Alice', 'mfa_enabled': True, 'days_since_pw_change': 120}]", call: "filter_at_risk_users([{'name': 'Bob', 'mfa_enabled': False, 'days_since_pw_change': 100}, {'name': 'Alice', 'mfa_enabled': True, 'days_since_pw_change': 120}])", expected: "[{'name': 'Bob', 'mfa_enabled': False, 'days_since_pw_change': 100}]" }
      ],
    },
    {
      id: 24,
      title: "Group Multi-Regional Store Sales",
      slug: "python-scenario-group-regional-sales",
      seoTitle: "Python Nested Region Sales Aggregation | Repractiq",
      metaDescription: "Group transactional sales records by region and store ID, aggregating overall performance totals.",
      tags: ["scenarios", "group by"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `group_regional_sales(records)` that parses sales transaction listings, grouping total revenues into a multi-layered dictionary categorized by `{'region': {'store_id': total_sales_revenue}}`.",
      explanation: "Grouping corporate sales data by region and store helps highlight top-performing markets and locations.",
      hint: "Use nested loops or dictionary structures to verify keys before accumulating transaction amounts.",
      starterCode: `def group_regional_sales(records):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'region': 'US', 'store_id': '101', 'rev': 500}, {'region': 'US', 'store_id': '101', 'rev': 200}]", call: "group_regional_sales([{'region': 'US', 'store_id': '101', 'rev': 500}, {'region': 'US', 'store_id': '101', 'rev': 200}])", expected: "{'US': {'101': 700}}" }
      ],
    },
    {
      id: 25,
      title: "Find Top N Network Bandwidth Nodes",
      slug: "python-scenario-top-n-network-nodes",
      seoTitle: "Python Network Volume Sorting Selection | Repractiq",
      metaDescription: "Extract the top bandwidth-consuming network nodes using clear secondary identifier sort keys.",
      tags: ["scenarios", "find top n", "sorting"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `top_bandwidth_nodes(nodes, n)` that returns the top `n` network nodes based on traffic volume. Resolve any volume ties by sorting the node ID strings alphabetically.",
      explanation: "Identifying top resource-consuming nodes helps prevent overall system congestion and optimizes server loads.",
      hint: "Sort using a primary descending numeric criteria and a secondary ascending alphabetical key.",
      starterCode: `def top_bandwidth_nodes(nodes, n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'id': 'node-B', 'gb': 500}, {'id': 'node-A', 'gb': 500}, {'id': 'node-C', 'gb': 1000}], 2", call: "top_bandwidth_nodes([{'id': 'node-B', 'gb': 500}, {'id': 'node-A', 'gb': 500}, {'id': 'node-C', 'gb': 1000}], 2)", expected: "[{'id': 'node-C', 'gb': 1000}, {'id': 'node-A', 'gb': 500}]" }
      ],
    },
    {
      id: 26,
      title: "Calculate Running Network Latency Variances",
      slug: "python-scenario-running-latency-variances",
      seoTitle: "Python Running Time-Series Deltas | Repractiq",
      metaDescription: "Compute a running delta list to analyze network performance changes between consecutive latency tests.",
      tags: ["scenarios", "running total"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `calculate_latency_variance(times)` that processes sequential network ping measurements, producing a list of the relative variance differences between each consecutive record.",
      explanation: "Calculating pings sequentially highlights spikes and patterns that can point to underlying network instability.",
      hint: "Calculate differences by subtracting index `i-1` values from the current index `i` item.",
      starterCode: `def calculate_latency_variance(times):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[50, 55, 45, 60]", call: "calculate_latency_variance([50, 55, 45, 60])", expected: "[5, -10, 15]" }
      ],
    },
    {
      id: 27,
      title: "Merge Overlapping Time Series Event Logs",
      slug: "python-scenario-merge-overlapping-time-logs",
      seoTitle: "Python Merge Chronological Event Intervals | Repractiq",
      metaDescription: "Consolidate overlapping event time windows into clean, non-overlapping interval sets.",
      tags: ["scenarios", "merge datasets", "sorting"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `merge_time_intervals(intervals)` that merges overlapping time range tuples (represented as tuples of start and end times), returning a clean list of combined, non-overlapping intervals.",
      explanation: "Consolidating timeline ranges helps optimize chronological data pipelines by preventing duplicate analysis.",
      hint: "Sort the intervals by their start times first, then iterate through to merge overlapping ranges.",
      starterCode: `def merge_time_intervals(intervals):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[(1, 4), (3, 6), (8, 10)]", call: "merge_time_intervals([(1, 4), (3, 6), (8, 10)])", expected: "[(1, 6), (8, 10)]" }
      ],
    },
    {
      id: 28,
      title: "Clean Dirty JSON API Key Paths",
      slug: "python-scenario-clean-dirty-api-keys",
      seoTitle: "Python API Field Type Cast Verification | Repractiq",
      metaDescription: "Clean up nested API JSON properties by fixing dynamic data types and stripping unwanted text characters.",
      tags: ["scenarios", "clean dirty data"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `clean_api_response(response)` that cleans API response data, ensuring all numeric status fields are properly cast to integers and removing special currency symbols from string values.",
      explanation: "Sanitizing dynamic API formats standardizes properties before they are passed down to internal functions.",
      hint: "Remove characters like `$` using `.replace('$', '')` before casting values to numeric types.",
      starterCode: `def clean_api_response(response):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'id': '101', 'amount': '$500.00'}", call: "clean_api_response({'id': '101', 'amount': '$500.00'})", expected: "{'id': 101, 'amount': 500.0}" }
      ],
    },
    {
      id: 29,
      title: "Format Dynamic Financial Progress Logs",
      slug: "python-scenario-format-financial-progress",
      seoTitle: "Python Multi Line Performance Console Display | Repractiq",
      metaDescription: "Format numeric statistics into aligned plain text tables for terminal-based reports.",
      tags: ["scenarios", "format reports"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `format_progress_report(progress, goal)` that takes current progress and overall goal numbers, generating a formatted text row showing raw values alongside their exact percentage completion rounded to two decimal places.",
      explanation: "Standardizing performance statistics into aligned text layouts makes progress updates easy to read at a glance.",
      hint: "Calculate percentage rates as: `(progress / goal) * 100` before formatting the output string.",
      starterCode: `def format_progress_report(progress, goal):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "75, 100", call: "format_progress_report(75, 100)", expected: "'75/100 (75.00%)'" }
      ],
    },
    {
      id: 30,
      title: "Aggregate Distributed Transaction Values",
      slug: "python-scenario-aggregate-distributed-transactions",
      seoTitle: "Python Nested Site Transaction Summary | Repractiq",
      metaDescription: "Aggregate total sales and taxes across multiple distributed retail location logs.",
      tags: ["scenarios", "aggregate metrics"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `aggregate_site_metrics(sites_data)` that summarizes total revenue and tax fields across multiple store metrics, calculating the final overall profitability.",
      explanation: "Aggregating sales and tax metrics across multiple stores helps pinpoint overall regional profitability.",
      hint: "Maintain running totals for sales and taxes, then calculate profitability as: `sales - tax`.",
      starterCode: `def aggregate_site_metrics(sites_data):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'sales': 1000, 'tax': 100}, {'sales': 2000, 'tax': 250}]", call: "aggregate_site_metrics([{'sales': 1000, 'tax': 100}, {'sales': 2000, 'tax': 250}])", expected: "{'total_sales': 3000, 'total_tax': 350, 'profit': 2650}" }
      ],
    },
    {
        id: 31,
        title: "Robust Log Parser for Malformed JSON",
        slug: "python-scenario-parse-malformed-json-logs",
        seoTitle: "Python Robust Malformed JSON Log Parser | Repractiq",
        metaDescription: "Parse and recover structured log entries from corrupted, partially malformed JSON string streams.",
        tags: ["scenarios", "data parsing", "error handling"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `parse_malformed_logs(log_lines)` that attempts to parse a list of JSON-formatted log strings. Skip rows that raise a `ValueError`, logging the count of skipped rows.",
        explanation: "Real-world log streams often contain partial or corrupted lines; defensive parsing ensures pipeline stability.",
        hint: "Use a `try-except` block within a loop, relying on `json.loads()` for parsing.",
        starterCode: `import json\n\ndef parse_malformed_logs(log_lines):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: '["{\\"id\\": 1}", "invalid_json"]', call: "parse_malformed_logs(['{\"id\": 1}', 'invalid_json'])", expected: "([{'id': 1}], 1)" }
        ],
      },
      {
        id: 32,
        title: "Weighted Moving Averages for Sensor Streams",
        slug: "python-scenario-weighted-moving-averages",
        seoTitle: "Python Weighted Moving Average Logic | Repractiq",
        metaDescription: "Calculate weighted moving averages over time-series sensor data to minimize noise impact.",
        tags: ["scenarios", "aggregation", "math"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `weighted_moving_average(data, weights)` that calculates the moving average of a dataset using a provided list of weights.",
        explanation: "Weighted averages prioritize recent or specific high-fidelity data points over older, less relevant noise.",
        hint: "Perform a dot product of the data window and the weight list at each iteration.",
        starterCode: `def weighted_moving_average(data, weights):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[10, 20, 30], [0.2, 0.3, 0.5]", call: "weighted_moving_average([10, 20, 30], [0.2, 0.3, 0.5])", expected: "[23.0]" }
        ],
      },
      {
        id: 33,
        title: "Filter Inventory by Multi-Nested Conditions",
        slug: "python-scenario-filter-nested-inventory",
        seoTitle: "Python Deeply Nested Filter Logic | Repractiq",
        metaDescription: "Filter inventory items based on deeply nested conditions such as warehouse location availability and stock thresholds.",
        tags: ["scenarios", "filtering", "nested data structures"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `filter_stock(inventory, min_stock, location)` that filters items where the item count at a specific `location` exceeds `min_stock`.",
        explanation: "Filtering deep tree-like structures is essential for managing multi-warehouse distributed inventory systems.",
        hint: "Access nested dicts safely using `item.get('stock', {}).get(location, 0)`.",
        starterCode: `def filter_stock(inventory, min_stock, location):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'name': 'A', 'stock': {'NYC': 50}}, {'name': 'B', 'stock': {'NYC': 5}}], 20, 'NYC'", call: "filter_stock([{'name': 'A', 'stock': {'NYC': 50}}, {'name': 'B', 'stock': {'NYC': 5}}], 20, 'NYC')", expected: "[{'name': 'A', 'stock': {'NYC': 50}}]" }
        ],
      },
      {
        id: 34,
        title: "Dynamic Grouping of Transactions by Categorical Keys",
        slug: "python-scenario-dynamic-transaction-grouping",
        seoTitle: "Python Dynamic Key Data Aggregator | Repractiq",
        metaDescription: "Group transactional datasets dynamically based on user-provided keys to generate flexible summary reports.",
        tags: ["scenarios", "group by"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `group_by_key(data, key)` that groups records by the specified dictionary key, accumulating the values into a list for each unique group.",
        explanation: "Data grouping functions allow analysts to slice datasets across any dimension, providing granular insights.",
        hint: "Iterate through the data and use a `defaultdict(list)` to append records to their respective keys.",
        starterCode: `from collections import defaultdict\n\ndef group_by_key(data, key):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'city': 'LA', 'val': 1}, {'city': 'NYC', 'val': 2}, {'city': 'LA', 'val': 3}], 'city'", call: "dict(group_by_key([{'city': 'LA', 'val': 1}, {'city': 'NYC', 'val': 2}, {'city': 'LA', 'val': 3}], 'city'))", expected: "{'LA': [{'city': 'LA', 'val': 1}, {'city': 'LA', 'val': 3}], 'NYC': [{'city': 'NYC', 'val': 2}]}" }
        ],
      },
      {
        id: 35,
        title: "Find Top N Items with Multi-Field Rank Sorting",
        slug: "python-scenario-top-n-multi-field-rank",
        seoTitle: "Python Multi Priority Sorting Algorithm | Repractiq",
        metaDescription: "Rank items based on primary numerical value and secondary timestamps for deterministic ordering.",
        tags: ["scenarios", "find top n", "sorting"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `rank_top_n(records, n)` that returns the top `n` records sorted by 'score' (descending) and 'created_at' (ascending) to break ties.",
        explanation: "Deterministic ranking ensures consistency, preventing erratic behavior in user-facing leaderboards.",
        hint: "Use a sort key of `(-record['score'], record['created_at'])`.",
        starterCode: `def rank_top_n(records, n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'score': 10, 'created_at': 2}, {'score': 10, 'created_at': 1}], 1", call: "rank_top_n([{'score': 10, 'created_at': 2}, {'score': 10, 'created_at': 1}], 1)", expected: "[{'score': 10, 'created_at': 1}]" }
        ],
      },
      {
        id: 36,
        title: "Calculate Running Total with Reset Logic",
        slug: "python-scenario-running-total-reset",
        seoTitle: "Python Conditional Running Total Reset | Repractiq",
        metaDescription: "Compute cumulative totals that reset to zero when a specific trigger condition is encountered.",
        tags: ["scenarios", "running total"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `resetting_running_total(values, reset_trigger)` that calculates a running total, resetting the count to zero whenever the current value matches `reset_trigger`.",
        explanation: "Resetting totals track session-specific gains or losses, vital for performance monitoring metrics.",
        hint: "Use an accumulator and a loop to update values, resetting the variable when necessary.",
        starterCode: `def resetting_running_total(values, reset_trigger):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[10, 20, 0, 5, 10], 0", call: "resetting_running_total([10, 20, 0, 5, 10], 0)", expected: "[10, 30, 0, 5, 15]" }
        ],
      },
      {
        id: 37,
        title: "Merge Nested Datasets with Conflict Handling",
        slug: "python-scenario-merge-nested-datasets",
        seoTitle: "Python Merge Logic Conflict Resolution | Repractiq",
        metaDescription: "Merge two dictionaries where nested updates must intelligently preserve unique sub-keys.",
        tags: ["scenarios", "merge datasets", "dictionaries"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `deep_merge(dict1, dict2)` that merges two dictionaries. For matching sub-keys, prioritize `dict1`'s values; otherwise, maintain all keys.",
        explanation: "Deep merging configurations ensures that application defaults remain intact while allowing for runtime modifications.",
        hint: "Iterate through `dict2` and check if a key exists in `dict1` before overwriting.",
        starterCode: `def deep_merge(dict1, dict2):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'a': 1}, {'b': 2}", call: "deep_merge({'a': 1}, {'b': 2})", expected: "{'a': 1, 'b': 2}" }
        ],
      },
      {
        id: 38,
        title: "Clean Dirty Time Format Strings",
        slug: "python-scenario-clean-dirty-time-formats",
        seoTitle: "Python Normalize Dirty Date Strings | Repractiq",
        metaDescription: "Normalize inconsistent date-time string formats into a standard ISO format for data ingestion.",
        tags: ["scenarios", "clean dirty data", "string manipulation"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `normalize_dates(dates)` that transforms strings like '1/1/2026' or 'Jan 1, 2026' into '2026-01-01'. Return 'Invalid' for unparseable strings.",
        explanation: "Standardizing heterogeneous date formats is the first step in cleaning unstructured data for analysis.",
        hint: "Use the `dateutil.parser` library for robust flexible date parsing.",
        starterCode: `from dateutil import parser\n\ndef normalize_dates(dates):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['1/1/2026', 'bad']", call: "normalize_dates(['1/1/2026', 'bad'])", expected: "['2026-01-01', 'Invalid']" }
        ],
      },
      {
        id: 39,
        title: "Format Markdown-Styled Metric Reports",
        slug: "python-scenario-format-markdown-reports",
        seoTitle: "Python Markdown Table Generation | Repractiq",
        metaDescription: "Generate professional, aligned markdown table strings from raw performance data dictionaries.",
        tags: ["scenarios", "format reports"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `format_markdown_table(data)` that produces a markdown string (including header and separator rows) from a list of record dictionaries.",
        explanation: "Markdown report generation creates portable, documentation-friendly summaries for automated data tools.",
        hint: "Identify the dictionary keys for the header, then iterate over list elements to construct pipe-separated rows.",
        starterCode: `def format_markdown_table(data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'key': 'A', 'val': 1}]", call: "format_markdown_table([{'key': 'A', 'val': 1}])", expected: "| key | val |\\n|---|---|\\n| A | 1 |" }
        ],
      },
      {
        id: 40,
        title: "Aggregate Dynamic Metrics Across Time Windows",
        slug: "python-scenario-aggregate-dynamic-windows",
        seoTitle: "Python Dynamic Aggregation Windowing | Repractiq",
        metaDescription: "Aggregate event logs dynamically into customizable time windows for performance trend analysis.",
        tags: ["scenarios", "aggregate metrics"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `aggregate_by_window(events, window_size)` that groups event counts into buckets based on a numeric timestamp threshold.",
        explanation: "Time-windowed aggregation simplifies high-frequency event monitoring into manageable trend reports.",
        hint: "Calculate the bucket using `timestamp // window_size` as the dictionary key.",
        starterCode: `def aggregate_by_window(events, window_size):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[10, 15, 20], 10", call: "aggregate_by_window([10, 15, 20], 10)", expected: "{1: 2, 2: 1}" }
        ],
      },
      {
        id: 41,
        title: "CSV String Parser with Escaped Characters",
        slug: "python-scenario-parse-escaped-csv",
        seoTitle: "Python CSV String Parser Escape Logic | Repractiq",
        metaDescription: "Parse CSV strings that include custom escape characters, ensuring accurate column separation.",
        tags: ["scenarios", "data parsing", "string manipulation"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `parse_escaped_csv(line)` that splits a CSV line, where comma characters within the string are prefixed with a backslash escape.",
        explanation: "Handling custom escape characters is required for sanitizing legacy database exports.",
        hint: "Use regex look-behind or a manual state tracker to split only on unescaped commas.",
        starterCode: `import re\n\ndef parse_escaped_csv(line):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'val1,val2\\\\,sub,val3'", call: "parse_escaped_csv('val1,val2\\\\,sub,val3')", expected: "['val1', 'val2,sub', 'val3']" }
        ],
      },
      {
        id: 42,
        title: "Calculate Moving Standard Deviation",
        slug: "python-scenario-moving-std-dev",
        seoTitle: "Python Moving Standard Deviation Calculation | Repractiq",
        metaDescription: "Compute the moving standard deviation of a dataset to evaluate data volatility.",
        tags: ["scenarios", "aggregation", "math"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `moving_std_dev(data, window_size)` that calculates the rolling standard deviation for a numeric dataset.",
        explanation: "Volatility metrics derived from moving standard deviation are essential for risk modeling and sensor health monitoring.",
        hint: "Use `statistics.stdev` on window slices.",
        starterCode: `import statistics\n\ndef moving_std_dev(data, window_size):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3], 2", call: "moving_std_dev([1, 2, 3], 2)", expected: "[0.7071067811865476, 0.7071067811865476]" }
        ],
      },
      {
        id: 43,
        title: "Filter Records by Complex Regex Pattern",
        slug: "python-scenario-filter-records-regex",
        seoTitle: "Python Filter by Regex Pattern | Repractiq",
        metaDescription: "Filter records by matching text fields against sophisticated regular expressions.",
        tags: ["scenarios", "filtering", "string manipulation"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `regex_filter(records, pattern)` that returns records where a specific field matches the given regex.",
        explanation: "Regex-based filtering is powerful for identifying unstructured data entries, such as IDs or codes.",
        hint: "Use the `re.search` function on each record's targeted text field.",
        starterCode: `import re\n\ndef regex_filter(records, pattern):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'log': 'err-1'}, {'log': 'inf-1'}], '^err'", call: "regex_filter([{'log': 'err-1'}, {'log': 'inf-1'}], '^err')", expected: "[{'log': 'err-1'}]" }
        ],
      },
      {
        id: 44,
        title: "Group Data by Multi-Level Attribute hierarchy",
        slug: "python-scenario-group-hierarchical",
        seoTitle: "Python Multi-Level Data Grouping | Repractiq",
        metaDescription: "Group complex datasets by multiple attribute levels to create a structured hierarchical summary.",
        tags: ["scenarios", "group by", "nested data structures"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `group_multi(data, keys)` that groups the data by a sequence of keys.",
        explanation: "Hierarchical grouping provides insight into multi-dimensional data relations, such as region and store departments.",
        hint: "Use recursion or a series of dictionary lookups to build the nested structure.",
        starterCode: `def group_multi(data, keys):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'r': 'A', 's': 'X'}], ['r', 's']", call: "group_multi([{'r': 'A', 's': 'X'}], ['r', 's'])", expected: "{'A': {'X': [{'r': 'A', 's': 'X'}]}}" }
        ],
      },
      {
        id: 45,
        title: "Find Top N Items using Heap Data Structure",
        slug: "python-scenario-top-n-heap",
        seoTitle: "Python Heap-Based Top N Items | Repractiq",
        metaDescription: "Efficiently identify the top N items in large datasets using min-heap for optimal performance.",
        tags: ["scenarios", "find top n", "performance"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `top_n_heap(data, n)` that returns the top `n` values using the `heapq` module.",
        explanation: "Min-heaps are significantly more efficient than full sorting for large datasets requiring only top N entries.",
        hint: "Use `heapq.nlargest` for optimal performance.",
        starterCode: `import heapq\n\ndef top_n_heap(data, n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 5, 2, 8, 3], 2", call: "top_n_heap([1, 5, 2, 8, 3], 2)", expected: "[8, 5]" }
        ],
      },
      {
        id: 46,
        title: "Calculate Cumulative Transaction Percentages",
        slug: "python-scenario-cumulative-percentage",
        seoTitle: "Python Cumulative Percentage Calculation | Repractiq",
        metaDescription: "Compute cumulative percentage totals for transaction data to visualize distribution weight.",
        tags: ["scenarios", "running total", "math"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `cumulative_percentage(values)` that returns a list of running cumulative percentages of the total sum.",
        explanation: "Cumulative percentages are vital for Pareto analyses (e.g., finding the top 80/20 of revenue).",
        hint: "Calculate the total sum first, then iterate while maintaining a running subtotal.",
        starterCode: `def cumulative_percentage(values):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[50, 50]", call: "cumulative_percentage([50, 50])", expected: "[0.5, 1.0]" }
        ],
      },
      {
        id: 47,
        title: "Merge Multi-Source JSON Data",
        slug: "python-scenario-merge-json-sources",
        seoTitle: "Python Merge JSON Dictionary Sources | Repractiq",
        metaDescription: "Merge disparate JSON objects into a single cohesive data structure while resolving key conflicts.",
        tags: ["scenarios", "merge datasets", "dictionaries"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `merge_json(json1, json2)` that merges two dictionaries. If key paths conflict, maintain the data from the newer source (`json2`).",
        explanation: "Data integration across different endpoints often requires robust conflict resolution to keep internal caches clean.",
        hint: "Python dictionaries `update()` method defaults to overwriting keys from source objects.",
        starterCode: `def merge_json(json1, json2):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'a': 1}, {'a': 2}", call: "merge_json({'a': 1}, {'a': 2})", expected: "{'a': 2}" }
        ],
      },
      {
        id: 48,
        title: "Clean Dirty Email Lists",
        slug: "python-scenario-clean-email-lists",
        seoTitle: "Python Validate Email String Formats | Repractiq",
        metaDescription: "Clean and validate email list strings, ensuring only well-formatted addresses are retained.",
        tags: ["scenarios", "clean dirty data"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `clean_emails(emails)` that returns a list of valid email addresses, converted to lowercase, removing any entries that don't match standard email format.",
        explanation: "Validated user datasets reduce bounced emails and prevent data quality issues in CRM systems.",
        hint: "Use regex `r'[^@]+@[^@]+\\.[^@]+'` to check format validity.",
        starterCode: `import re\n\ndef clean_emails(emails):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['User@Test.com', 'bad']", call: "clean_emails(['User@Test.com', 'bad'])", expected: "['user@test.com']" }
        ],
      },
      {
        id: 49,
        title: "Generate CSV-Styled Log Reports",
        slug: "python-scenario-csv-report-generator",
        seoTitle: "Python Generate Formatted CSV Report | Repractiq",
        metaDescription: "Generate a formatted CSV-like report string from arbitrary dictionaries, including header resolution.",
        tags: ["scenarios", "format reports"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `generate_csv(data)` that takes a list of dicts and produces a CSV string where columns are alphabetically sorted.",
        explanation: "CSV generation is a core requirement for exporting report data for use in external tools like Excel.",
        hint: "Sort dictionary keys for consistent output, then use `csv.DictWriter`.",
        starterCode: `import csv, io\n\ndef generate_csv(data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'a': 1, 'b': 2}]", call: "generate_csv([{'a': 1, 'b': 2}])", expected: "'a,b\\n1,2\\n'" }
        ],
      },
      {
        id: 50,
        title: "Aggregate Performance Metrics by Group ID",
        slug: "python-scenario-aggregate-group-metrics",
        seoTitle: "Python Group-Based Metric Aggregator | Repractiq",
        metaDescription: "Compute aggregate metrics per group ID, useful for multi-threaded performance monitoring.",
        tags: ["scenarios", "aggregate metrics"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `aggregate_group_stats(records)` that summarizes the mean performance for each 'group_id'.",
        explanation: "Group-level performance metrics are essential for identifying bottlenecks in large distributed systems.",
        hint: "Group items into lists by `group_id`, then compute the `statistics.mean` for each list.",
        starterCode: `import statistics\n\ndef aggregate_group_stats(records):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'group_id': 1, 'val': 10}, {'group_id': 1, 'val': 20}]", call: "aggregate_group_stats([{'group_id': 1, 'val': 10}, {'group_id': 1, 'val': 20}])", expected: "{1: 15.0}" }
        ],
      },
      {
        id: 51,
        title: "Robust CSV Parser with Type Casting",
        slug: "python-scenario-typed-csv-parser",
        seoTitle: "Python CSV Typed Column Parser | Repractiq",
        metaDescription: "Parse CSV strings while casting columns to specific types (int, float, str) based on a schema.",
        tags: ["scenarios", "data parsing"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `typed_csv_parse(line, schema)` where `schema` is a list of type functions (e.g., `[int, float, str]`).",
        explanation: "Typed parsing significantly improves pipeline speed and prevents runtime errors during analysis.",
        hint: "Map the `schema` function list over the split items using `zip`.",
        starterCode: `def typed_csv_parse(line, schema):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'1,1.5,val', [int, float, str]", call: "typed_csv_parse('1,1.5,val', [int, float, str])", expected: "[1, 1.5, 'val']" }
        ],
      },
      {
        id: 52,
        title: "Sliding Average of Sensor Fluctuations",
        slug: "python-scenario-sliding-sensor-avg",
        seoTitle: "Python Sliding Window Sensor Logic | Repractiq",
        metaDescription: "Calculate the sliding average of sensor data, focusing on identifying significant shifts.",
        tags: ["scenarios", "aggregation"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `sliding_avg(stream, n)` that calculates the moving average, ignoring values outside 2 standard deviations.",
        explanation: "Filtering outliers before calculating averages ensures trends remain stable in noisy sensor environments.",
        hint: "Use the `statistics` module to compute the mean and standard deviation for filtering.",
        starterCode: `import statistics\n\ndef sliding_avg(stream, n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[10, 11, 10, 100], 3", call: "sliding_avg([10, 11, 10, 100], 3)", expected: "[10.5]" }
        ],
      },
      {
        id: 53,
        title: "Filter Records with Dynamic Lambda Conditions",
        slug: "python-scenario-lambda-filter",
        seoTitle: "Python Lambda-Based Record Filtering | Repractiq",
        metaDescription: "Filter datasets dynamically using passed lambda functions for highly flexible query operations.",
        tags: ["scenarios", "filtering"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `filter_with_lambda(records, condition)` that returns items meeting the dynamic lambda `condition`.",
        explanation: "Dynamic filtering allows for reusable utility functions capable of handling varied criteria.",
        hint: "Pass the lambda directly to the `filter()` function.",
        starterCode: `def filter_with_lambda(records, condition):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3], lambda x: x > 1", call: "filter_with_lambda([1, 2, 3], lambda x: x > 1)", expected: "[2, 3]" }
        ],
      },
      {
        id: 54,
        title: "Group Data by Time Granularity",
        slug: "python-scenario-group-by-time",
        seoTitle: "Python Time-Granular Data Grouping | Repractiq",
        metaDescription: "Group transactional data by hourly or daily granularity for performance reporting.",
        tags: ["scenarios", "group by"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `group_by_hour(events)` that groups events by their hour component.",
        explanation: "Aggregating data by time granularity is foundational for creating hour-by-hour usage charts.",
        hint: "Extract the hour from the timestamp string and use a `defaultdict(list)`.",
        starterCode: `from collections import defaultdict\n\ndef group_by_hour(events):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'time': '2026-01-01 10:00:00'}, {'time': '2026-01-01 10:30:00'}]", call: "dict(group_by_hour([{'time': '2026-01-01 10:00:00'}, {'time': '2026-01-01 10:30:00'}]))", expected: "{'10': [{'time': '2026-01-01 10:00:00'}, {'time': '2026-01-01 10:30:00'}]}" }
        ],
      },
      {
        id: 55,
        title: "Top N Most Expensive Transactions",
        slug: "python-scenario-top-n-expensive",
        seoTitle: "Python Expensive Transaction Filter | Repractiq",
        metaDescription: "Filter and sort the top N most expensive transactions from a list of logs.",
        tags: ["scenarios", "find top n", "sorting"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `top_expensive(txs, n)` that sorts by transaction 'amount' descending.",
        explanation: "Identifying the highest value transactions is crucial for audit and fraud prevention workflows.",
        hint: "Use `sorted(txs, key=lambda x: x['amount'], reverse=True)[:n]`.",
        starterCode: `def top_expensive(txs, n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'amount': 100}, {'amount': 500}], 1", call: "top_expensive([{'amount': 100}, {'amount': 500}], 1)", expected: "[{'amount': 500}]" }
        ],
      },
      {
        id: 56,
        title: "Calculate Running Median of Streams",
        slug: "python-scenario-running-median",
        seoTitle: "Python Streaming Median Algorithm | Repractiq",
        metaDescription: "Compute a rolling median of numerical data streams, suitable for real-time monitoring.",
        tags: ["scenarios", "running total"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `running_median(stream)` that yields the median of the data seen so far.",
        explanation: "The median is more robust than the average against extreme values (outliers) in data streams.",
        hint: "Use two heaps (min and max) to maintain the median efficiently in O(log n).",
        starterCode: `import heapq\n\ndef running_median(stream):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 5, 2]", call: "list(running_median([1, 5, 2]))", expected: "[1.0, 3.0, 2.0]" }
        ],
      },
      {
        id: 57,
        title: "Merge Nested Configs with User Overrides",
        slug: "python-scenario-merge-configs",
        seoTitle: "Python Configuration Merge Logic | Repractiq",
        metaDescription: "Merge system configurations with user-provided overrides while maintaining hierarchy.",
        tags: ["scenarios", "merge datasets"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `merge_configs(sys_cfg, user_cfg)` that returns the merged config object.",
        explanation: "Maintaining clean configuration overrides allows for granular user control without modifying base system settings.",
        hint: "Use dictionary unpacking `{**sys_cfg, **user_cfg}` for shallow merges.",
        starterCode: `def merge_configs(sys_cfg, user_cfg):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'v': 1}, {'v': 2}", call: "merge_configs({'v': 1}, {'v': 2})", expected: "{'v': 2}" }
        ],
      },
      {
        id: 58,
        title: "Clean Dirty JSON String Keys",
        slug: "python-scenario-clean-json-keys",
        seoTitle: "Python Sanitize JSON Key Strings | Repractiq",
        metaDescription: "Clean JSON keys, normalizing special characters to snake_case format.",
        tags: ["scenarios", "clean dirty data"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `sanitize_json_keys(data)` that converts all keys to lowercase and replaces spaces with underscores.",
        explanation: "Key normalization ensures consistent database property names, preventing issues with case-sensitive systems.",
        hint: "Rebuild the dictionary by applying `k.lower().replace(' ', '_')` to keys.",
        starterCode: `def sanitize_json_keys(data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'User Name': 1}", call: "sanitize_json_keys({'User Name': 1})", expected: "{'user_name': 1}" }
        ],
      },
      {
        id: 59,
        title: "Format Tabular Financial Ledger",
        slug: "python-scenario-format-ledger",
        seoTitle: "Python Financial Ledger Table Formatter | Repractiq",
        metaDescription: "Format financial transaction records into neatly aligned tabular string output.",
        tags: ["scenarios", "format reports"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `format_ledger(records)` that produces a formatted, fixed-width string for ledger logs.",
        explanation: "Aligned tabular logs make financial auditing significantly faster by improving data readability.",
        hint: "Use f-string padding, like `f'{val:<10}'`.",
        starterCode: `def format_ledger(records):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'id': 1, 'amount': 100}]", call: "format_ledger([{'id': 1, 'amount': 100}])", expected: "'ID: 1        Amount: 100   '" }
        ],
      },
      {
        id: 60,
        title: "Aggregate Distributed Inventory Counts",
        slug: "python-scenario-aggregate-inventory",
        seoTitle: "Python Inventory Aggregation Across Warehouses | Repractiq",
        metaDescription: "Aggregate item counts across multiple distributed warehouses to get a total company inventory count.",
        tags: ["scenarios", "aggregate metrics"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `total_inventory(warehouse_data)` that summarizes stock by item name.",
        explanation: "Aggregation across warehouses provides a unified view, crucial for order management and supply chain planning.",
        hint: "Iterate through warehouses and use a dictionary to sum counts for each item.",
        starterCode: `def total_inventory(warehouse_data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'A': 10}, {'A': 20}]", call: "total_inventory([{'A': 10}, {'A': 20}])", expected: "{'A': 30}" }
        ],
      },

        {
          id: 61,
          title: "Graph-Based Dependency Resolver",
          slug: "python-scenario-graph-dependency-resolver",
          seoTitle: "Python Graph Dependency Resolver | Repractiq",
          metaDescription: "Resolve package installation order using topological sorting on a dependency graph.",
          tags: ["scenarios", "algorithms", "graphs"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `resolve_dependencies(graph)` that returns a list of nodes in a valid topological order.",
          explanation: "Topological sorting is essential for resolving build orders and package dependencies in complex software systems.",
          hint: "Use Kahn's algorithm or recursive DFS with post-order traversal.",
          starterCode: `from collections import defaultdict, deque\n\ndef resolve_dependencies(graph):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "{'A': ['B'], 'B': []}", call: "resolve_dependencies({'A': ['B'], 'B': []})", expected: "['A', 'B']" }
          ],
        },
        {
          id: 62,
          title: "State Machine for Order Processing",
          slug: "python-scenario-order-state-machine",
          seoTitle: "Python Finite State Machine Logic | Repractiq",
          metaDescription: "Implement a robust finite state machine to manage complex order transitions.",
          tags: ["scenarios", "state machine", "logic"],
          difficulty: "Hard",
          type: "function",
          description: "Write a `StateMachine` class that handles state transitions and raises an error for invalid transitions (e.g., 'Cancelled' -> 'Shipped').",
          explanation: "Finite State Machines ensure that business objects follow strict lifecycle rules, preventing corrupt data states.",
          hint: "Use a dictionary to define valid transitions: `{'current': ['allowed_next1', 'allowed_next2']}`.",
          starterCode: `class StateMachine:\n    def __init__(self, transitions):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 63,
          title: "LRU Cache Implementation",
          slug: "python-scenario-lru-cache",
          seoTitle: "Python Custom LRU Cache Implementation | Repractiq",
          metaDescription: "Build a Least Recently Used (LRU) cache mechanism to optimize performance for repetitive function calls.",
          tags: ["scenarios", "caching", "data structures"],
          difficulty: "Hard",
          type: "function",
          description: "Implement a `LRUCache` class with `get(key)` and `put(key, value)` methods that operate in O(1) time.",
          explanation: "An LRU cache is fundamental to performance engineering in web servers and database query optimizers.",
          hint: "Combine a `dict` for O(1) access and an `OrderedDict` (or doubly linked list) to track usage order.",
          starterCode: `from collections import OrderedDict\n\nclass LRUCache:\n    def __init__(self, capacity):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 64,
          title: "Interval Merge Algorithm",
          slug: "python-scenario-interval-merge",
          seoTitle: "Python Merge Overlapping Intervals | Repractiq",
          metaDescription: "Merge overlapping time intervals into a consolidated set of non-overlapping time blocks.",
          tags: ["scenarios", "algorithms", "math"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `merge_intervals(intervals)` that merges overlapping lists (e.g., `[[1,3],[2,6]]` -> `[[1,6]]`).",
          explanation: "Interval merging is common in scheduling, calendar management, and resource allocation applications.",
          hint: "Sort intervals by start time, then iterate to merge if `current_start <= previous_end`.",
          starterCode: `def merge_intervals(intervals):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "[[1,3], [2,6], [8,10]]", call: "merge_intervals([[1,3], [2,6], [8,10]])", expected: "[[1,6], [8,10]]" }
          ],
        },
        {
          id: 65,
          title: "Trie (Prefix Tree) Implementation",
          slug: "python-scenario-trie-implementation",
          seoTitle: "Python Trie Data Structure | Repractiq",
          metaDescription: "Implement a Trie data structure to support efficient prefix-based searching and autocomplete.",
          tags: ["scenarios", "data structures", "search"],
          difficulty: "Hard",
          type: "function",
          description: "Implement `Trie` class with `insert(word)` and `search(prefix)` methods.",
          explanation: "Tries are optimized for string search operations, especially when many words share common prefixes.",
          hint: "Use a nested dictionary structure where each key is a character.",
          starterCode: `class Trie:\n    def __init__(self):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 66,
          title: "Priority Queue Task Scheduler",
          slug: "python-scenario-priority-queue-scheduler",
          seoTitle: "Python Priority Queue Task Scheduler | Repractiq",
          metaDescription: "Develop a task scheduler using a priority queue to ensure critical jobs are executed first.",
          tags: ["scenarios", "algorithms", "queues"],
          difficulty: "Hard",
          type: "function",
          description: "Write a `TaskScheduler` that manages tasks with associated priority levels using the `heapq` module.",
          explanation: "Priority queues ensure that system resources are allocated to the most time-sensitive tasks.",
          hint: "Use `heapq.heappush` and `heapq.heappop` for O(log n) operations.",
          starterCode: `import heapq\n\nclass TaskScheduler:\n    def __init__(self):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 67,
          title: "Find Largest K-th Element in Unsorted Stream",
          slug: "python-scenario-find-kth-element",
          seoTitle: "Python K-th Element Search Algorithm | Repractiq",
          metaDescription: "Find the K-th largest element in a large, potentially unsorted stream of data without full sorting.",
          tags: ["scenarios", "algorithms", "search"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `find_kth_largest(nums, k)` that finds the K-th largest number.",
          explanation: "This algorithm avoids O(n log n) sorting, using a min-heap to keep track of top K elements in O(n log k) time.",
          hint: "Maintain a min-heap of size K.",
          starterCode: `import heapq\n\ndef find_kth_largest(nums, k):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "[3,2,1,5,6,4], 2", call: "find_kth_largest([3,2,1,5,6,4], 2)", expected: "5" }
          ],
        },
        {
          id: 68,
          title: "Backtracking Sudoku Solver",
          slug: "python-scenario-sudoku-solver",
          seoTitle: "Python Backtracking Sudoku Solver | Repractiq",
          metaDescription: "Write an algorithmic solver for Sudoku puzzles using backtracking.",
          tags: ["scenarios", "algorithms", "backtracking"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `solve_sudoku(board)` that fills in a 9x9 Sudoku board.",
          explanation: "Backtracking is a powerful technique for solving constraint-satisfaction problems.",
          hint: "Use recursion to try digits 1-9; if an invalid path is found, revert the choice.",
          starterCode: `def solve_sudoku(board):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 69,
          title: "Rate Limiter with Token Bucket",
          slug: "python-scenario-rate-limiter",
          seoTitle: "Python Rate Limiter Token Bucket | Repractiq",
          metaDescription: "Implement a token bucket rate limiter to control the flow of requests in a web service.",
          tags: ["scenarios", "systems design", "performance"],
          difficulty: "Hard",
          type: "function",
          description: "Write a `RateLimiter` class that allows a set number of requests within a defined time window.",
          explanation: "Rate limiting is crucial for preventing abuse and managing load on backend services.",
          hint: "Use `time.time()` and a token count to track usage since the last reset.",
          starterCode: `import time\n\nclass RateLimiter:\n    def __init__(self, rate, per):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 70,
          title: "Flatten Nested JSON Generator",
          slug: "python-scenario-flatten-json",
          seoTitle: "Python Flatten Nested JSON Dictionary | Repractiq",
          metaDescription: "Recursively flatten a deeply nested JSON object into a single-level dictionary.",
          tags: ["scenarios", "recursive", "json"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `flatten(data)` that turns `{'a': {'b': 1}}` into `{'a.b': 1}`.",
          explanation: "Flattening is often required to load nested JSON data into relational database tables.",
          hint: "Use recursion with a prefix accumulator string.",
          starterCode: `def flatten(data, parent_key='', sep='.'):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "{'a': {'b': 1}}", call: "flatten({'a': {'b': 1}})", expected: "{'a.b': 1}" }
          ],
        },
        {
          id: 71,
          title: "Dijkstra's Shortest Path Algorithm",
          slug: "python-scenario-dijkstra-shortest-path",
          seoTitle: "Python Dijkstra Shortest Path | Repractiq",
          metaDescription: "Implement Dijkstra's algorithm to find the shortest path between nodes in a weighted graph.",
          tags: ["scenarios", "algorithms", "graphs"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `dijkstra(graph, start)` that returns the minimum distance to all nodes.",
          explanation: "Dijkstra is the gold standard for routing and navigation algorithms.",
          hint: "Use `heapq` for an efficient priority queue of frontier nodes.",
          starterCode: `import heapq\n\ndef dijkstra(graph, start):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 72,
          title: "Circular Buffer for Data Streams",
          slug: "python-scenario-circular-buffer",
          seoTitle: "Python Circular Buffer Implementation | Repractiq",
          metaDescription: "Implement a fixed-size circular buffer for high-speed data stream processing.",
          tags: ["scenarios", "data structures", "queues"],
          difficulty: "Hard",
          type: "function",
          description: "Write a `CircularBuffer` class that overwrites the oldest element when full.",
          explanation: "Circular buffers efficiently handle streams of fixed size without constant memory allocation.",
          hint: "Use a list of fixed size and a pointer to track the current index.",
          starterCode: `class CircularBuffer:\n    def __init__(self, size):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 73,
          title: "Binary Tree Depth Calculator",
          slug: "python-scenario-tree-depth",
          seoTitle: "Python Binary Tree Depth Algorithm | Repractiq",
          metaDescription: "Determine the maximum depth of a binary tree structure.",
          tags: ["scenarios", "algorithms", "trees"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `max_depth(node)` that returns the height of the tree.",
          explanation: "Calculating tree depth is vital for analyzing tree-based data structures' balance.",
          hint: "Recursive approach: `1 + max(max_depth(left), max_depth(right))`.",
          starterCode: `def max_depth(node):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 194,
          title: "Anagram Groups Resolver",
          slug: "python-scenario-group-anagrams",
          seoTitle: "Python Anagram Groups Algorithm | Repractiq",
          metaDescription: "Group sets of words that are anagrams of each other.",
          tags: ["scenarios", "algorithms", "hashmaps"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `group_anagrams(words)` that returns groups of anagrams.",
          explanation: "Normalizing strings to a sorted state (or character count tuple) acts as a unique hash key for anagram grouping.",
          hint: "Use `collections.defaultdict(list)` with `tuple(sorted(word))` as the key.",
          starterCode: `from collections import defaultdict\n\ndef group_anagrams(words):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "['eat', 'tea', 'tan']", call: "group_anagrams(['eat', 'tea', 'tan'])", expected: "[['eat', 'tea'], ['tan']]" }
          ],
        },
        {
          id: 75,
          title: "Weighted Random Selector",
          slug: "python-scenario-weighted-random",
          seoTitle: "Python Weighted Random Selector | Repractiq",
          metaDescription: "Select items randomly based on specified weight probabilities.",
          tags: ["scenarios", "random", "math"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `weighted_choice(choices, weights)` that picks a choice based on relative weights.",
          explanation: "Weighted selection is widely used in A/B testing and load balancing scenarios.",
          hint: "Use `random.choices` or calculate cumulative sums and `random.random()`.",
          starterCode: `import random\n\ndef weighted_choice(choices, weights):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 76,
          title: "Merge Multi-Path Sorted Streams",
          slug: "python-scenario-merge-sorted-streams",
          seoTitle: "Python K-Way Merge Algorithm | Repractiq",
          metaDescription: "Efficiently merge multiple sorted input streams into one unified sorted output stream.",
          tags: ["scenarios", "algorithms", "sorting"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `merge_k_sorted(streams)` that returns a combined generator.",
          explanation: "K-way merging is the foundation of external merge sorts, critical for out-of-memory data processing.",
          hint: "Use a min-heap to always grab the smallest head element from the active streams.",
          starterCode: `import heapq\n\ndef merge_k_sorted(streams):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 77,
          title: "Expression Evaluator (RPN)",
          slug: "python-scenario-rpn-evaluator",
          seoTitle: "Python RPN Calculator | Repractiq",
          metaDescription: "Evaluate Reverse Polish Notation (RPN) mathematical expressions using a stack.",
          tags: ["scenarios", "stack", "math"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `eval_rpn(tokens)` that evaluates a list of string tokens.",
          explanation: "RPN evaluation is a standard way to parse mathematical formulas in programming languages.",
          hint: "Use a stack: push numbers, pop two when an operator is encountered.",
          starterCode: `def eval_rpn(tokens):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "['2', '1', '+', '3', '*']", call: "eval_rpn(['2', '1', '+', '3', '*'])", expected: "9" }
          ],
        },
        {
          id: 78,
          title: "Detect Cycle in Linked List",
          slug: "python-scenario-cycle-detection",
          seoTitle: "Python Linked List Cycle Detection | Repractiq",
          metaDescription: "Use Floyd's cycle-finding algorithm to detect cycles in linked lists.",
          tags: ["scenarios", "algorithms", "linked lists"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `has_cycle(head)` that returns true if a cycle exists.",
          explanation: "Memory-efficient cycle detection is essential for graph traversal where cycles may cause infinite loops.",
          hint: "Use two pointers moving at different speeds (tortoise and hare).",
          starterCode: `def has_cycle(head):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 79,
          title: "Longest Increasing Subsequence",
          slug: "python-scenario-longest-subsequence",
          seoTitle: "Python Longest Increasing Subsequence | Repractiq",
          metaDescription: "Find the length of the longest increasing subsequence in an unsorted list.",
          tags: ["scenarios", "algorithms", "dynamic programming"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `lis(nums)` that finds the LIS length.",
          explanation: "Dynamic programming is required to optimize this from O(n^2) to O(n log n) in some cases.",
          hint: "Use an array to store the smallest tail for every subsequence length.",
          starterCode: `import bisect\n\ndef lis(nums):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "[10,9,2,5,3,7,101,18]", call: "lis([10,9,2,5,3,7,101,18])", expected: "4" }
          ],
        },
        {
          id: 80,
          title: "Matrix Rotator 90 Degrees",
          slug: "python-scenario-matrix-rotation",
          seoTitle: "Python Matrix In-Place Rotation | Repractiq",
          metaDescription: "Rotate an N x N matrix 90 degrees clockwise in-place.",
          tags: ["scenarios", "algorithms", "matrices"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `rotate(matrix)` to rotate the matrix.",
          explanation: "In-place matrix rotation is a classic interview challenge testing spatial awareness and array manipulation.",
          hint: "Transpose the matrix first, then reverse each row.",
          starterCode: `def rotate(matrix):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 81,
          title: "Trie-Based Autocomplete Suggestion",
          slug: "python-scenario-autocomplete",
          seoTitle: "Python Trie Autocomplete Logic | Repractiq",
          metaDescription: "Develop an autocomplete service that suggests words based on a prefix using a Trie.",
          tags: ["scenarios", "search", "data structures"],
          difficulty: "Hard",
          type: "function",
          description: "Extend your Trie class with a method `get_suggestions(prefix)`.",
          explanation: "Efficient prefix lookup is the foundation of high-performance search and recommendation systems.",
          hint: "DFS from the node reached by the prefix to gather all possible suffixes.",
          starterCode: `class AutocompleteTrie(Trie):\n    def get_suggestions(self, prefix):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 82,
          title: "Job Batch Processing with Dependencies",
          slug: "python-scenario-job-batch-dependencies",
          seoTitle: "Python Job Processing Dependency Scheduler | Repractiq",
          metaDescription: "Schedule batches of jobs where some jobs require others to complete first.",
          tags: ["scenarios", "algorithms", "jobs"],
          difficulty: "Hard",
          type: "function",
          description: "Create a job runner that respects job dependencies (like a light DAG).",
          explanation: "Managing dependent tasks ensures data integrity in batch processing pipelines.",
          hint: "Use topological sorting on the job dependencies graph.",
          starterCode: `def run_jobs(jobs):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 83,
          title: "K-Nearest Neighbors Algorithm",
          slug: "python-scenario-knn-simple",
          seoTitle: "Python K-Nearest Neighbors Implementation | Repractiq",
          metaDescription: "Implement the k-Nearest Neighbors algorithm to classify a new data point.",
          tags: ["scenarios", "machine learning", "math"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `knn(data, point, k)` that returns the label of the closest neighbors.",
          explanation: "KNN is a fundamental supervised machine learning algorithm.",
          hint: "Calculate Euclidean distance from the point to all items, then sort by distance.",
          starterCode: `import math\n\ndef knn(data, point, k):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 84,
          title: "Web Crawler Depth Tracker",
          slug: "python-scenario-web-crawler-depth",
          seoTitle: "Python Crawler Depth Traversal | Repractiq",
          metaDescription: "Perform a BFS to track crawl depth on a mock web network.",
          tags: ["scenarios", "algorithms", "traversal"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `crawl(url, max_depth)` that performs a BFS traversal.",
          explanation: "BFS is essential for network crawling, ensuring we visit pages level-by-level.",
          hint: "Use `collections.deque` and track `(url, depth)` tuples.",
          starterCode: `from collections import deque\n\ndef crawl(start_url, max_depth):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 85,
          title: "Median of Two Sorted Arrays",
          slug: "python-scenario-median-sorted-arrays",
          seoTitle: "Python Median of Two Sorted Arrays | Repractiq",
          metaDescription: "Find the median of two sorted arrays efficiently (O(log(min(n, m)))).",
          tags: ["scenarios", "algorithms", "math"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `find_median(a, b)`.",
          explanation: "This tests binary search application to partition arrays correctly.",
          hint: "Perform a binary search on the shorter array to find the correct partition.",
          starterCode: `def find_median(a, b):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "[1, 3], [2]", call: "find_median([1, 3], [2])", expected: "2.0" }
          ],
        },
        {
          id: 86,
          title: "Implement Thread-Safe Counter",
          slug: "python-scenario-thread-safe-counter",
          seoTitle: "Python Thread-Safe Counter | Repractiq",
          metaDescription: "Create a thread-safe counter using locks for concurrent environments.",
          tags: ["scenarios", "threading", "concurrency"],
          difficulty: "Hard",
          type: "function",
          description: "Implement a `ThreadSafeCounter` with increment and value methods.",
          explanation: "Concurrency primitives like `threading.Lock` are required to prevent race conditions.",
          hint: "Acquire a lock before updating the count variable.",
          starterCode: `import threading\n\nclass ThreadSafeCounter:\n    def __init__(self):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 87,
          title: "Min-Cost Path in Grid",
          slug: "python-scenario-min-cost-path",
          seoTitle: "Python Min-Cost Path Algorithm | Repractiq",
          metaDescription: "Find the minimum cost path from top-left to bottom-right of a grid.",
          tags: ["scenarios", "dynamic programming", "grids"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `min_path_sum(grid)`.",
          explanation: "Dynamic programming simplifies finding optimal paths in discrete grid spaces.",
          hint: "Fill a 2D table where each cell `dp[i][j]` equals `grid[i][j] + min(dp[i-1][j], dp[i][j-1])`.",
          starterCode: `def min_path_sum(grid):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [
            { input: "[[1,3],[1,5]]", call: "min_path_sum([[1,3],[1,5]])", expected: "7" }
          ],
        },
        {
          id: 88,
          title: "Generate All Permutations",
          slug: "python-scenario-permutations",
          seoTitle: "Python Generate Permutations Algorithm | Repractiq",
          metaDescription: "Generate all possible permutations of a given list of items.",
          tags: ["scenarios", "algorithms", "recursion"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `permute(nums)`.",
          explanation: "Recursive permutation generation explores all possible reorderings of data.",
          hint: "Use recursion to build paths and track used indices.",
          starterCode: `def permute(nums):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 89,
          title: "Merge Intervals in Log File",
          slug: "python-scenario-merge-log-intervals",
          seoTitle: "Python Merge Log Time Intervals | Repractiq",
          metaDescription: "Parse log lines for start/end times and merge overlapping log events.",
          tags: ["scenarios", "data parsing", "intervals"],
          difficulty: "Hard",
          type: "function",
          description: "Write a function `process_logs(log_lines)` to merge duration intervals.",
          explanation: "Consolidating log timestamps provides a clean view of system uptime or operation status.",
          hint: "Parse strings to integers first, then use your `merge_intervals` function.",
          starterCode: `def process_logs(log_lines):\n    # your code here\n    pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
          id: 90,
          title: "Dynamic User Privilege Validator",
          slug: "python-scenario-rbac-validator",
          seoTitle: "Python RBAC Privilege Validator | Repractiq",
          metaDescription: "Implement a dynamic Role-Based Access Control (RBAC) validator for user roles.",
          tags: ["scenarios", "rbac", "security"],
          difficulty: "Hard",
          type: "function",
          description: "Write a class `PrivilegeValidator` that checks if a user's roles grant access to an action.",
          explanation: "RBAC is a standard security practice for managing granular user access to system resources.",
          hint: "Map roles to sets of allowed actions for O(1) lookups.",
          starterCode: `class PrivilegeValidator:\n    def __init__(self, roles_map):\n        # your code here\n        pass\n`,
          expectedOutput: "",
          testCases: [],
        },
        {
            id: 91,
            title: "Graph Shortest Path (Dijkstra)",
            slug: "python-scenario-dijkstra-path",
            seoTitle: "Python Dijkstra Shortest Path | Repractiq",
            metaDescription: "Implement Dijkstra's algorithm to find the shortest path in a weighted graph.",
            tags: ["scenarios", "algorithms", "graphs"],
            difficulty: "Hard",
            type: "function",
            description: "Write a function `find_shortest_path(graph, start, end)` using a priority queue.",
            explanation: "Dijkstra is the fundamental algorithm for pathfinding in network routing and maps.",
            hint: "Use `heapq` to efficiently pick the node with the smallest tentative distance.",
            starterCode: `import heapq\n\ndef find_shortest_path(graph, start, end):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 92,
            title: "LRU Cache Implementation",
            slug: "python-scenario-lru-cache",
            seoTitle: "Python LRU Cache from Scratch | Repractiq",
            metaDescription: "Build a Least Recently Used (LRU) cache using a dictionary and doubly linked list.",
            tags: ["scenarios", "data structures", "caching"],
            difficulty: "Hard",
            type: "class",
            description: "Create an `LRUCache` class with `get(key)` and `put(key, value)` methods, both O(1).",
            explanation: "LRU caches are essential for optimizing performance in memory-constrained environments.",
            hint: "Combine a hash map for O(1) access and a doubly linked list for O(1) ordering.",
            starterCode: `class LRUCache:\n    def __init__(self, capacity):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 93,
            title: "Thread-Safe Singleton Logger",
            slug: "python-scenario-thread-safe-logger",
            seoTitle: "Python Thread-Safe Singleton | Repractiq",
            metaDescription: "Implement a thread-safe singleton logger class.",
            tags: ["scenarios", "concurrency", "architecture"],
            difficulty: "Hard",
            type: "class",
            description: "Ensure only one instance of the `Logger` exists across multiple threads.",
            explanation: "Singletons control resource access, and thread safety prevents race conditions.",
            hint: "Use a locking mechanism (`threading.Lock`) during instantiation.",
            starterCode: `import threading\n\nclass Logger:\n    _instance = None\n    _lock = threading.Lock()\n\n    def __new__(cls):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 94,
            title: "Async File Downloader",
            slug: "python-scenario-async-downloader",
            seoTitle: "Python Async File Downloader | Repractiq",
            metaDescription: "Build an asynchronous file downloader that handles multiple URLs concurrently.",
            tags: ["scenarios", "concurrency", "io"],
            difficulty: "Hard",
            type: "function",
            description: "Write `async_download(urls)` using `aiohttp` to download files.",
            explanation: "Asynchronous I/O is critical for building scalable network scrapers and services.",
            hint: "Use `asyncio.gather` with `aiohttp.ClientSession`.",
            starterCode: `import asyncio\nimport aiohttp\n\nasync def async_download(urls):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 95,
            title: "Tree Traversal Aggregator",
            slug: "python-scenario-tree-aggregator",
            seoTitle: "Python Tree Traversal | Repractiq",
            metaDescription: "Perform a post-order traversal to calculate tree-wide metrics.",
            tags: ["scenarios", "algorithms", "trees"],
            difficulty: "Hard",
            type: "function",
            description: "Write `calculate_tree_sum(root)` to recursively sum all node values.",
            explanation: "Recursive traversal is the basis for most hierarchical data processing tasks.",
            hint: "Base case: if node is None, return 0. Else, return node.val + sum(children).",
            starterCode: `class Node:\n    def __init__(self, val, children=None):\n        self.val = val\n        self.children = children or []\n\ndef calculate_tree_sum(root):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 96,
            title: "Expression Evaluator",
            slug: "python-scenario-expr-evaluator",
            seoTitle: "Python Expression Evaluator | Repractiq",
            metaDescription: "Implement a parser to evaluate mathematical expressions with order of operations.",
            tags: ["scenarios", "algorithms", "parsing"],
            difficulty: "Hard",
            type: "function",
            description: "Write `evaluate(expression)` to compute result of string math formulas.",
            explanation: "Shunting-yard or recursive descent parsing is used in building compilers/interpreters.",
            hint: "Use stacks to maintain operators and operands.",
            starterCode: `def evaluate(expression):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 97,
            title: "Bloom Filter (Approximate Set)",
            slug: "python-scenario-bloom-filter",
            seoTitle: "Python Bloom Filter | Repractiq",
            metaDescription: "Implement a Bloom Filter to test membership in a massive dataset with low memory.",
            tags: ["scenarios", "algorithms", "data structures"],
            difficulty: "Hard",
            type: "class",
            description: "Write a `BloomFilter` class that uses multiple hash functions.",
            explanation: "Bloom filters offer high-performance probabilistic membership testing.",
            hint: "Use `mmh3` or similar hash functions and a bit array.",
            starterCode: `class BloomFilter:\n    def __init__(self, size, hash_count):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 98,
            title: "Trie (Prefix Tree) Implementation",
            slug: "python-scenario-trie",
            seoTitle: "Python Trie Data Structure | Repractiq",
            metaDescription: "Implement a Trie for fast prefix-based string searching.",
            tags: ["scenarios", "data structures", "search"],
            difficulty: "Hard",
            type: "class",
            description: "Create a `Trie` class with `insert(word)` and `starts_with(prefix)`.",
            explanation: "Tries are standard for autocomplete and predictive search functionality.",
            hint: "Each node contains a dictionary of children.",
            starterCode: `class Trie:\n    def __init__(self):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 99,
            title: "Wait-Free Queue",
            slug: "python-scenario-queue",
            seoTitle: "Python Queue | Repractiq",
            metaDescription: "Implement a thread-safe producer-consumer queue using `Condition` variables.",
            tags: ["scenarios", "concurrency", "systems"],
            difficulty: "Hard",
            type: "class",
            description: "Write a `SafeQueue` class that blocks until items are available.",
            explanation: "Condition variables allow threads to wait for specific state changes.",
            hint: "Use `threading.Condition()` to manage state and wake threads.",
            starterCode: `import threading\n\nclass SafeQueue:\n    def __init__(self):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 100,
            title: "Custom Data Serialization",
            slug: "python-scenario-serializer",
            seoTitle: "Python Custom Serializer | Repractiq",
            metaDescription: "Write a robust custom serializer to convert complex Python objects to bytes.",
            tags: ["scenarios", "data engineering", "io"],
            difficulty: "Hard",
            type: "function",
            description: "Write `serialize_obj(obj)` and `deserialize_obj(data)` functions.",
            explanation: "Custom serialization is required when standard `pickle` is insecure or unsuitable.",
            hint: "Use `struct` or `json` with recursive schema handling.",
            starterCode: `import json\n\ndef serialize_obj(obj):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 101,
            title: "Dynamic Task Scheduler",
            slug: "python-scenario-scheduler",
            seoTitle: "Python Task Scheduler | Repractiq",
            metaDescription: "Implement a priority-based task scheduler.",
            tags: ["scenarios", "algorithms", "systems"],
            difficulty: "Hard",
            type: "class",
            description: "Write a `Scheduler` that runs tasks based on priority and timestamp.",
            explanation: "Schedulers are essential in task-queue systems like Celery or Airflow.",
            hint: "Use `heapq` to keep tasks sorted by execution time.",
            starterCode: `import heapq\n\nclass Scheduler:\n    def add_task(self, task, priority):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 102,
            title: "Matrix Multiplication Performance",
            slug: "python-scenario-matrix-mult",
            seoTitle: "Python Matrix Multiplier | Repractiq",
            metaDescription: "Implement efficient matrix multiplication.",
            tags: ["scenarios", "algorithms", "math"],
            difficulty: "Hard",
            type: "function",
            description: "Write `multiply(matrix_a, matrix_b)` that optimizes inner loops.",
            explanation: "Matrix math is core to machine learning model performance.",
            hint: "For large matrices, consider transposing the second matrix first.",
            starterCode: `def multiply(mat1, mat2):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 103,
            title: "Distributed Lock Manager",
            slug: "python-scenario-dist-lock",
            seoTitle: "Python Distributed Lock | Repractiq",
            metaDescription: "Create a mock distributed lock manager for shared resources.",
            tags: ["scenarios", "systems design", "concurrency"],
            difficulty: "Hard",
            type: "class",
            description: "Create a `DistributedLock` class that uses a time-to-live (TTL) approach.",
            explanation: "Distributed locks coordinate access to shared databases in microservices.",
            hint: "Use timestamps and a mock central store (like Redis/KV store).",
            starterCode: `class DistributedLock:\n    def acquire(self, lock_name, ttl):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 104,
            title: "Log File Rolling Archive",
            slug: "python-scenario-log-archiver",
            seoTitle: "Python Log Archiver | Repractiq",
            metaDescription: "Build a utility that rotates and compresses old log files.",
            tags: ["scenarios", "data engineering", "io"],
            difficulty: "Hard",
            type: "function",
            description: "Write `rotate_logs(log_dir)` to compress files older than 30 days.",
            explanation: "Automated log cleanup prevents system storage saturation.",
            hint: "Use `os` and `shutil` for file movement and `gzip` for compression.",
            starterCode: `import os\nimport gzip\n\ndef rotate_logs(log_dir):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 105,
            title: "SQL Query Parser to AST",
            slug: "python-scenario-sql-ast",
            seoTitle: "Python SQL Parser | Repractiq",
            metaDescription: "Convert a subset of SQL queries into an Abstract Syntax Tree (AST).",
            tags: ["scenarios", "algorithms", "databases"],
            difficulty: "Hard",
            type: "function",
            description: "Write `parse_sql(query)` that returns a nested dict representing the query.",
            explanation: "ASTs are the first step in building query planners and optimizers.",
            hint: "Use regex or a library like `sqlparse` to tokenize.",
            starterCode: `def parse_sql(query):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 106,
            title: "Wait-Free Resource Pool",
            slug: "python-scenario-resource-pool",
            seoTitle: "Python Resource Pool | Repractiq",
            metaDescription: "Implement a connection pool for managing DB handles efficiently.",
            tags: ["scenarios", "systems design", "databases"],
            difficulty: "Hard",
            type: "class",
            description: "Create a `ResourcePool` with `checkout()` and `checkin()` methods.",
            explanation: "Connection pooling is key to efficient resource utilization in web apps.",
            hint: "Use a `Queue` for available resources and a `Lock` for safety.",
            starterCode: `import queue\n\nclass ResourcePool:\n    def __init__(self, size):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 107,
            title: "Stateful Data Processor",
            slug: "python-scenario-stateful-proc",
            seoTitle: "Python Stateful Processor | Repractiq",
            metaDescription: "Process data with memory of previous inputs to detect pattern shifts.",
            tags: ["scenarios", "data engineering", "monitoring"],
            difficulty: "Hard",
            type: "class",
            description: "Create a `Processor` class that tracks moving stats over time.",
            explanation: "Detecting changes (concept drift) is essential in real-time modeling.",
            hint: "Maintain a running mean/variance state.",
            starterCode: `class Processor:\n    def process(self, record):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 108,
            title: "Binary Tree Serializer",
            slug: "python-scenario-tree-serialize",
            seoTitle: "Python Tree Serializer | Repractiq",
            metaDescription: "Serialize and deserialize a binary tree structure.",
            tags: ["scenarios", "algorithms", "data structures"],
            difficulty: "Hard",
            type: "function",
            description: "Write `serialize(root)` and `deserialize(data)`.",
            explanation: "Tree serialization allows persistence and transport of graph-based structures.",
            hint: "Use preorder or level-order traversal with placeholder for None.",
            starterCode: `def serialize(root):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 109,
            title: "Event-Loop Scheduler",
            slug: "python-scenario-event-loop",
            seoTitle: "Python Event Loop | Repractiq",
            metaDescription: "Implement a basic event loop from scratch for task processing.",
            tags: ["scenarios", "systems design", "concurrency"],
            difficulty: "Hard",
            type: "class",
            description: "Write a loop that polls tasks and executes them.",
            explanation: "Understanding event loops is key to mastering Python's `asyncio`.",
            hint: "Use `collections.deque` and a while loop.",
            starterCode: `from collections import deque\n\nclass EventLoop:\n    def run(self, tasks):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 110,
            title: "K-Means Clustering (Scratch)",
            slug: "python-scenario-kmeans",
            seoTitle: "Python K-Means | Repractiq",
            metaDescription: "Implement K-means clustering algorithm from scratch.",
            tags: ["scenarios", "algorithms", "data science"],
            difficulty: "Hard",
            type: "function",
            description: "Write `kmeans(points, k)` to find cluster centroids.",
            explanation: "K-means is the standard unsupervised learning algorithm for grouping.",
            hint: "Repeatedly assign points to nearest centroid and update centroids.",
            starterCode: `import math\n\ndef kmeans(points, k):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 111,
            title: "Bit Manipulation Hash",
            slug: "python-scenario-bit-hash",
            seoTitle: "Python Bit Hash | Repractiq",
            metaDescription: "Implement a custom string hashing function using bitwise ops.",
            tags: ["scenarios", "algorithms", "optimization"],
            difficulty: "Hard",
            type: "function",
            description: "Write `hash_string(s)` using bit shifts and XOR.",
            explanation: "Fast non-cryptographic hashes are used in hash maps and Bloom filters.",
            hint: "Use `ord()` for characters and apply bitwise operations.",
            starterCode: `def hash_string(s):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 112,
            title: "Graph Cycle Detector (DFS)",
            slug: "python-scenario-cycle-detect",
            seoTitle: "Python Cycle Detection | Repractiq",
            metaDescription: "Detect cycles in a directed graph using Depth First Search.",
            tags: ["scenarios", "algorithms", "graphs"],
            difficulty: "Hard",
            type: "function",
            description: "Write `has_cycle(graph)` to check for circular dependencies.",
            explanation: "Cycle detection prevents infinite loops in directed dependency chains.",
            hint: "Maintain `visited` and `recursion_stack` sets.",
            starterCode: `def has_cycle(graph):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 113,
            title: "Max-Flow Algorithm (Ford-Fulkerson)",
            slug: "python-scenario-max-flow",
            seoTitle: "Python Ford-Fulkerson | Repractiq",
            metaDescription: "Find the maximum flow in a network.",
            tags: ["scenarios", "algorithms", "optimization"],
            difficulty: "Hard",
            type: "function",
            description: "Write `max_flow(graph, source, sink)`.",
            explanation: "Max-flow finds the capacity bottlenecks in logistics and networks.",
            hint: "Repeatedly find augmenting paths using BFS/DFS.",
            starterCode: `def max_flow(graph, source, sink):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 114,
            title: "Persistent Key-Value Store",
            slug: "python-scenario-kv-store",
            seoTitle: "Python KV Store | Repractiq",
            metaDescription: "Build a disk-backed key-value store.",
            tags: ["scenarios", "systems design", "databases"],
            difficulty: "Hard",
            type: "class",
            description: "Create a class that reads/writes key-values to a local file.",
            explanation: "Basic KV stores are the building blocks of more complex databases.",
            hint: "Use `open(file, 'r+b')` and manage file pointers.",
            starterCode: `class KVStore:\n    def set(self, key, val):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 115,
            title: "Sliding Window Maximum",
            slug: "python-scenario-sliding-max",
            seoTitle: "Python Sliding Window Max | Repractiq",
            metaDescription: "Find the maximum element in every sliding window of a stream.",
            tags: ["scenarios", "algorithms", "optimization"],
            difficulty: "Hard",
            type: "function",
            description: "Write `get_sliding_max(nums, k)` with O(N) complexity.",
            explanation: "Efficient window processing is essential for high-performance signal analysis.",
            hint: "Use a `collections.deque` to keep track of indices of max elements.",
            starterCode: `from collections import deque\n\ndef get_sliding_max(nums, k):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 116,
            title: "Wait-Free Producer-Consumer",
            slug: "python-scenario-wait-free",
            seoTitle: "Python Wait-Free | Repractiq",
            metaDescription: "Use `queue.Queue` to build an efficient data pipeline.",
            tags: ["scenarios", "concurrency", "architecture"],
            difficulty: "Hard",
            type: "class",
            description: "Implement a producer/consumer pattern using threads.",
            explanation: "Decoupling data generation from processing maximizes system throughput.",
            hint: "Use `threading.Thread` and a thread-safe queue.",
            starterCode: `import queue\nimport threading\n\nclass Pipeline:\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 117,
            title: "Memory Profiler Wrapper",
            slug: "python-scenario-memory-prof",
            seoTitle: "Python Memory Profiler | Repractiq",
            metaDescription: "Write a decorator that profiles memory usage of a function.",
            tags: ["scenarios", "optimization", "tools"],
            difficulty: "Hard",
            type: "function",
            description: "Create `@profile_memory` to print memory delta.",
            explanation: "Profiling identifies bottlenecks in memory-intensive operations.",
            hint: "Use `tracemalloc` to track memory changes.",
            starterCode: `import tracemalloc\n\ndef profile_memory(func):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 118,
            title: "Graph Component Counter",
            slug: "python-scenario-components",
            seoTitle: "Python Graph Components | Repractiq",
            metaDescription: "Count the number of disconnected components in an undirected graph.",
            tags: ["scenarios", "algorithms", "graphs"],
            difficulty: "Hard",
            type: "function",
            description: "Write `count_components(graph)`.",
            explanation: "Component counting is used to analyze connectivity and network robustness.",
            hint: "Use BFS/DFS traversal to visit all nodes in each connected sub-graph.",
            starterCode: `def count_components(graph):\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 119,
            title: "Data Stream Median",
            slug: "python-scenario-stream-median",
            seoTitle: "Python Streaming Median | Repractiq",
            metaDescription: "Calculate the median of a stream in O(log N) time.",
            tags: ["scenarios", "algorithms", "stats"],
            difficulty: "Hard",
            type: "class",
            description: "Create `MedianFinder` with `add_num()` and `find_median()`.",
            explanation: "Tracking running medians is a common task in real-time analytics.",
            hint: "Use two heaps (max-heap for smaller half, min-heap for larger half).",
            starterCode: `import heapq\n\nclass MedianFinder:\n    # your code here\n    pass\n`,
            expectedOutput: "",
            testCases: [],
          },
          {
            id: 120,
            title: "Custom Context Manager",
            slug: "python-scenario-context-mgr",
            seoTitle: "Python Context Manager | Repractiq",
            metaDescription: "Develop a custom context manager for resource-heavy objects.",
            tags: ["scenarios", "architecture", "io"],
            difficulty: "Hard",
            type: "class",
            description: "Create a `ManagedResource` that ensures cleanup in `__exit__`.",
            explanation: "Resource management is a pillar of safe, production-grade software.",
            hint: "Use the `@contextlib.contextmanager` decorator or `__enter__`/`__exit__` methods.",
            starterCode: `class ManagedResource:\n    def __enter__(self):\n        # your code here\n        pass\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
            expectedOutput: "",
            testCases: [],
          },
  ];